import React, { useEffect, useState } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useOrgStore } from '@/store/orgStore';
import api from '@/api';
import {
  allowsDemoReset,
  authLabel,
  dataLabel,
  isRuntimeMisconfigured,
  isSupabaseAuthMode,
  surfaceLabel,
} from '@/config/runtime';
import {
  LayoutDashboard, Users, Contact, Building2, Target,
  Workflow, CheckSquare, BarChart3, Settings, ChevronLeft,
  ChevronRight, Search, Bell, LogOut, ChevronDown, Menu, X, RefreshCw, ShieldCheck, ShieldAlert
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import ReleaseNotesButton from '@/components/ReleaseNotesButton';

const navItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/leads', label: 'Leads', icon: Users },
  { path: '/contactos', label: 'Contactos', icon: Contact },
  { path: '/empresas', label: 'Empresas', icon: Building2 },
  { path: '/oportunidades', label: 'Oportunidades', icon: Target },
  { path: '/pipeline', label: 'Pipeline', icon: Workflow },
  { path: '/actividades', label: 'Actividades', icon: CheckSquare },
  { path: '/reportes', label: 'Reportes', icon: BarChart3 },
  { path: '/configuracion', label: 'Configuracion', icon: Settings },
];

export default function DashboardLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<any | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { currentOrgName, organizations, setCurrentOrg } = useOrgStore();
  const breadcrumbs = [
    { label: 'Dashboard', path: '/' },
  ];
  if (location.pathname.startsWith('/dashboard/')) {
    breadcrumbs.push({ label: 'Detalle', path: location.pathname });
  } else if (location.pathname.startsWith('/empresas/')) {
    breadcrumbs.push({ label: 'Empresas', path: '/empresas' }, { label: 'Detalle', path: location.pathname });
  } else if (location.pathname === '/empresas') {
    breadcrumbs.push({ label: 'Empresas', path: '/empresas' });
  } else if (location.pathname === '/leads') {
    breadcrumbs.push({ label: 'Leads', path: '/leads' });
  } else if (location.pathname === '/oportunidades') {
    breadcrumbs.push({ label: 'Oportunidades', path: '/oportunidades' });
  } else if (location.pathname.startsWith('/oportunidades/')) {
    breadcrumbs.push({ label: 'Oportunidades', path: '/oportunidades' }, { label: 'Detalle', path: location.pathname });
  } else if (location.pathname === '/actividades') {
    breadcrumbs.push({ label: 'Actividades', path: '/actividades' });
  } else if (location.pathname.startsWith('/leads/')) {
    breadcrumbs.push({ label: 'Leads', path: '/leads' }, { label: 'Detalle', path: location.pathname });
  }

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleResetDemoData = () => {
    window.localStorage.removeItem('conectacrm.local.auth.user');
    window.localStorage.removeItem('conectacrm.local.db.v1');
    window.localStorage.removeItem('currentOrgId');
    window.localStorage.removeItem('currentOrgName');
    window.localStorage.removeItem('conectacrm.e2e.user');
    window.location.reload();
  };

  useEffect(() => {
    const run = async () => {
      const q = search.trim();
      if (q.length < 2) {
        setSearchResults(null);
        return;
      }
      const { data } = await api.get('/search', { params: { q } });
      setSearchResults(data);
    };
    const id = window.setTimeout(() => { run().catch(() => setSearchResults(null)); }, 250);
    return () => window.clearTimeout(id);
  }, [search]);

  const NavContent = () => (
    <>
      <div className="h-16 flex items-center px-4 border-b border-[#E2E8F0] shrink-0">
        {!collapsed && (
          <span className="text-xl font-black tracking-tight text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Conecta<span className="text-[#2563EB]">CRM</span>
          </span>
        )}
        {collapsed && <span className="text-xl font-black text-[#2563EB] mx-auto">C</span>}
      </div>
      <nav className="flex-1 py-4 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`sidebar-${item.label.toLowerCase()}-link`}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 px-4 py-2.5 mx-2 rounded-md transition-colors duration-150 ${
                isActive
                  ? 'bg-[#2563EB]/10 text-[#2563EB] font-medium'
                  : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
              }`}
            >
              <Icon size={20} className="shrink-0" />
              {!collapsed && <span className="text-sm">{item.label}</span>}
            </Link>
          );
        })}
      </nav>
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="hidden md:flex items-center justify-center p-3 border-t border-[#E2E8F0] hover:bg-[#F1F5F9] transition-colors shrink-0"
        data-testid="sidebar-collapse-btn"
      >
        {collapsed ? <ChevronRight size={20} className="text-[#475569]" /> : <ChevronLeft size={20} className="text-[#475569]" />}
      </button>
    </>
  );

  return (
    <div className="flex h-screen bg-[#F8FAFC]" data-testid="dashboard-layout">
      {/* Desktop Sidebar */}
      <aside className={`${collapsed ? 'w-20' : 'w-64'} bg-white/95 backdrop-blur-xl border-r border-[#E2E8F0] h-screen fixed top-0 left-0 transition-all duration-300 z-40 hidden md:flex flex-col shadow-[0_20px_60px_rgba(15,23,42,0.08)]`}>
        <NavContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="fixed inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="fixed left-0 top-0 h-full w-64 bg-white z-50 flex flex-col shadow-2xl">
            <NavContent />
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className={`flex-1 ${collapsed ? 'md:ml-20' : 'md:ml-64'} transition-all duration-300 flex flex-col min-h-screen`}>
        {/* Header */}
        <header className="h-16 border-b border-[#E2E8F0] bg-white/80 backdrop-blur-xl sticky top-0 z-30 flex items-center justify-between gap-2 px-4 md:px-6 shrink-0">
          <div className="flex min-w-0 items-center gap-2 md:gap-3">
            <button className="md:hidden p-1 shrink-0" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
            <div className="hidden xl:flex items-center gap-2 shrink-0">
              <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-700">
                <span>{surfaceLabel}</span>
              </div>
              <div className={`flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] ${
                isSupabaseAuthMode
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  : isRuntimeMisconfigured
                    ? 'border-rose-200 bg-rose-50 text-rose-700'
                    : 'border-amber-200 bg-amber-50 text-amber-700'
              }`}>
                {isSupabaseAuthMode ? <ShieldCheck size={13} /> : <ShieldAlert size={13} />}
                <span>{authLabel}</span>
              </div>
              <div className="flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-sky-700">
                <span>{dataLabel}</span>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2 border-[#E2E8F0] text-sm font-medium shrink-0 min-w-0" data-testid="org-selector">
                  <Building2 size={15} className="text-[#2563EB] shrink-0" />
                  <span className="max-w-[110px] sm:max-w-[160px] truncate">{currentOrgName || 'Seleccionar org.'}</span>
                  <ChevronDown size={14} className="text-[#94A3B8] shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {organizations.map((org) => (
                  <DropdownMenuItem key={org.org_id} onClick={() => setCurrentOrg(org.org_id, org.org_name)} data-testid={`org-option-${org.org_id}`}>
                    {org.org_name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="relative hidden lg:block min-w-0 flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <Input
                placeholder="Buscar empresa, contacto, lead..."
                className="pl-9 w-full max-w-72 h-9 bg-[#F8FAFC] border-[#E2E8F0] text-sm"
                data-testid="global-search"
                value={search}
                onFocus={() => setSearchOpen(true)}
                onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
                onChange={(e) => setSearch(e.target.value)}
              />
              {searchOpen && searchResults && search.trim().length >= 2 && (
                <div className="absolute top-11 left-0 w-96 max-h-96 overflow-auto rounded-lg border border-[#E2E8F0] bg-white shadow-xl z-50">
                  {searchResults.companies?.length > 0 && (
                    <div className="p-2">
                      <p className="px-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-[#94A3B8]">Empresas</p>
                      {searchResults.companies.map((item: any) => (
                        <Link key={item.id} to={`/empresas/${item.id}`} className="block px-3 py-2 rounded-md hover:bg-[#F8FAFC]" onClick={() => setSearch('')}>
                          <p className="text-sm font-medium text-[#0F172A]">{item.name}</p>
                          <p className="text-xs text-[#475569]">{item.rut || item.industry || 'Empresa'}</p>
                        </Link>
                      ))}
                    </div>
                  )}
                  {searchResults.contacts?.length > 0 && (
                    <div className="p-2 border-t border-[#E2E8F0]">
                      <p className="px-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-[#94A3B3]">Contactos</p>
                      {searchResults.contacts.map((item: any) => (
                        <div key={item.id} className="px-3 py-2 rounded-md hover:bg-[#F8FAFC]">
                          <p className="text-sm font-medium text-[#0F172A]">{item.name}</p>
                          <p className="text-xs text-[#475569]">{item.position || item.email}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {searchResults.leads?.length > 0 && (
                    <div className="p-2 border-t border-[#E2E8F0]">
                      <p className="px-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-[#94A3B3]">Leads</p>
                      {searchResults.leads.map((item: any) => (
                        <div key={item.id} className="px-3 py-2 rounded-md hover:bg-[#F8FAFC]">
                          <p className="text-sm font-medium text-[#0F172A]">{item.name}</p>
                          <p className="text-xs text-[#475569]">{item.company || item.status}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <ReleaseNotesButton compact className="hidden xl:inline-flex" />
            {allowsDemoReset && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetDemoData}
                className="hidden sm:inline-flex gap-2 border-[#E2E8F0] text-[#475569]"
                data-testid="reset-demo-data-btn"
              >
                <RefreshCw size={14} />
                Reset demo
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="notifications-btn">
              <Bell size={18} className="text-[#475569]" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 h-9 px-2" data-testid="user-menu">
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="bg-[#2563EB] text-white text-xs font-semibold">
                      {user && typeof user === 'object' ? user.name?.charAt(0)?.toUpperCase() : 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline text-sm font-medium text-[#0F172A]">
                    {user && typeof user === 'object' ? user.name : ''}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate('/configuracion')}>
                  <Settings size={15} className="mr-2" /> Configuracion
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                  <LogOut size={15} className="mr-2" /> Cerrar Sesion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 min-w-0 p-4 md:p-6 lg:p-8 overflow-x-hidden overflow-y-auto">
          <div className="mb-4 flex items-center gap-2 text-xs text-[#64748B]">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.path}>
                <Link to={crumb.path} className="hover:text-[#2563EB]">
                  {crumb.label}
                </Link>
                {index < breadcrumbs.length - 1 && <span>/</span>}
              </React.Fragment>
            ))}
          </div>
          <div className="min-w-0 rounded-3xl border border-white/60 bg-white/70 backdrop-blur-xl p-3 sm:p-4 md:p-6 shadow-[0_20px_60px_rgba(15,23,42,0.06)]">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
